import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import {
  CustomDialogProps,
  SmartForm,
  SmartSubmit,
  SmartTextField,
  useSmartForm,
} from "@nodeon/react-components";
import { useSnackbar } from "notistack";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiResponse,
  ComparisonLevelListDto,
  useComparisonLevelApi,
} from "~/api/GeneratedApi";

interface FormProps {
  name: string;
}

interface Props {
  comparisonLevel?: ComparisonLevelListDto;
  onSubmitted?: (name: string) => void;
  initialValue?: string;
}

const ComparisonLevelDialog = ({
  handleClose,
  onSubmitted,
  comparisonLevel,
  initialValue,
}: CustomDialogProps & Props): JSX.Element => {
  const { editComparisonLevel, addComparisonLevel } = useComparisonLevelApi();
  const { t } = useTranslation("comparison-level");
  const { enqueueSnackbar } = useSnackbar();

  const edit = useMemo(() => !!comparisonLevel, [comparisonLevel]);

  const onSuccess = (data: FormProps, res: ApiResponse) => {
    enqueueSnackbar(t(`apiResponses:${res.resultKey}`), { variant: "success" });
    onSubmitted?.(data.name);
    handleClose?.();
  };

  const onSubmit = (data: FormProps) => {
    return edit
      ? editComparisonLevel(
          {
            name: data.name,
          },
          comparisonLevel!.id,
        )
      : addComparisonLevel({
          name: data.name,
        });
  };

  const defaultValues = useMemo(
    () => ({ name: comparisonLevel?.name ?? initialValue ?? "" }),
    [comparisonLevel?.name, initialValue],
  );

  const formMethods = useSmartForm<FormProps, ApiResponse>({
    defaultValues,
  });

  return (
    <Dialog open onClose={handleClose}>
      <SmartForm<FormProps, ApiResponse>
        onSubmit={onSubmit}
        noGrid
        onSuccess={(data, res) => onSuccess(data, res)}
        methods={formMethods}
      >
        <DialogTitle id="form-dialog-title">
          {edit ? t("edit-title") : t("create-title")}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SmartTextField<FormProps>
                fullWidth
                name="name"
                label={t("name")}
                required
                autoFocus
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {t("general:cancel")}
          </Button>
          <SmartSubmit disabled={edit ? !formMethods.formState.isDirty : false}>
            {edit ? t("general:edit") : t("general:create")}
          </SmartSubmit>
        </DialogActions>
      </SmartForm>
    </Dialog>
  );
};

export default ComparisonLevelDialog;
